import { useState, useEffect } from 'react';
import { Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import moment from 'moment';
import useResponsive from '../../../utils/responsive';
import localStorageService from '../../services/storage-service/local-storage.service';
import { HOMII_KEYS } from '../../../utils/constants';

const PROMO_MODAL_SESSION_KEY = HOMII_KEYS.PROMO_MODAL_SESSION_KEY;

const isWithinDateRange = (startDateStr, endDateStr) => {    

    if (!startDateStr || !endDateStr) {
        return false;
    }    

    const startDate = moment(startDateStr).toDate();
    const endDate = moment(endDateStr).toDate();
    const today = moment();

    const isWithinRange =
        today.isSameOrAfter(startDate, 'day') &&
        today.isSameOrBefore(endDate, 'day');

    return isWithinRange;
};

const PromotionalModal = ({ promotionData }) => {
    const { isMobile } = useResponsive();
    const [isOpen, setIsOpen] = useState(false);

    const {
        promotion_start_date,
        promotion_end_date,        
        promotion_desktop_background_url,
        promotion_mobile_background_url,
        promotion_title,
        promotion_text,
    } = promotionData || {};

    useEffect(() => {
        if (!isWithinDateRange(promotion_start_date, promotion_end_date)) return;

        try {
            const alreadyShown = sessionStorage.getItem(PROMO_MODAL_SESSION_KEY);
            if (alreadyShown) return;
        } catch { /* ignore */ }

        const imageUrl = isMobile
            ? promotion_mobile_background_url
            : promotion_desktop_background_url;
        const hasBackground = !!imageUrl;

        if (!hasBackground) {
            setIsOpen(true);
            return;
        }

        const img = new window.Image();
        img.onload = () => setIsOpen(true);
        img.onerror = () => setIsOpen(true);
        img.src = imageUrl;

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [
        promotion_start_date,
        promotion_end_date,        
        promotion_desktop_background_url,
        promotion_mobile_background_url,
        isMobile,
    ]);

    const handleClose = () => {
        try {
            sessionStorage.setItem(PROMO_MODAL_SESSION_KEY, 'true');
        } catch { /* ignore */ }
        setIsOpen(false);
    };

    if (!promotionData) return null;

    const backgroundUrl = isMobile
        ? promotion_mobile_background_url
        : promotion_desktop_background_url;
    const showBackground = !!backgroundUrl;

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            aria-labelledby="promotional-modal-title"
            aria-describedby="promotional-modal-text"
        >
            <div
                className={`promotional-modal-content${isMobile ? ' promotional-modal-content-mobile' : ''}`}
                style={showBackground ? { backgroundImage: `url(${backgroundUrl})` } : undefined}
            >
                {showBackground && <div className="promotional-modal-overlay" />}

                <IconButton
                    className="promotional-modal-close-button"
                    disableRipple
                    aria-label="close"
                    onClick={handleClose}
                >
                    <CloseIcon />
                </IconButton>

                <div className={`promotional-modal-body${isMobile ? '-mobile' : ''}`}>
                    {promotion_title && (
                        <h3 id="promotional-modal-title" className="promotional-modal-title">
                            {promotion_title}
                        </h3>
                    )}
                    {promotion_text && (
                        <p id="promotional-modal-text" className="promotional-modal-text">
                            {promotion_text}
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default PromotionalModal;

PromotionalModal.propTypes = {
    promotionData: PropTypes.shape({
        promotion_start_date: PropTypes.string,
        promotion_end_date: PropTypes.string,        
        promotion_desktop_background_url: PropTypes.string,
        promotion_mobile_background_url: PropTypes.string,
        promotion_title: PropTypes.string,
        promotion_text: PropTypes.string,
    }),
};
